import { find, findIndex, first, invoke, remove } from 'lodash';
import { DataStoreMockPlugin } from 'ui-common';
import { v4 } from 'uuid';
import {
  AgentConfiguration,
  AgentStudioComponent,
  AgentStudioTemplate,
  ComponentType,
  IndexTarget,
  SolutionPack,
  TemplateDatastore,
} from '../../../components/app-studio/agent-studio/interfaces';

export class AgentStudioStoreMockPlugin extends DataStoreMockPlugin {
  mocks: any;
  mockApis: string[] = [
    'getComponents',
    'getComponentType',
    'getAISolutionPacks',
    'createDatastore',
    'getDatastores',
    'getDatastore',
    'updateDatastore',
    'deleteDatastore',
    'getIndexTargets',
    'getIndexTarget',
    'createIndexTarget',
    'updateIndexTarget',
    'deleteIndexTarget',
    'getIndexTargetStats',
    'getAgentConfiguration',
    'getAgentConfigurations',
    'deleteAgentConfiguration',
    'updateAgentConfiguration',
    'createAgentConfiguration',
    'getNewestTemplateVersion',
    'getTemplateHistory',
    ...DataStoreMockPlugin.crudMockApis,
  ];

  /**
   * @override
   */
  async getMockData(): Promise<AgentStudioTemplate[]> {
    this.mocks ??= await import('./agent-studio-store-mock-data');
    return this.mocks.templates;
  }

  /**
   * @override
   */
  async addItem(templateData: any): Promise<AgentStudioTemplate> {
    // the store maps the data to the templates object
    // it should handle the templates object update internally
    const { data, idProp } = this.store.meta;
    
    // Determine component configuration based on template type
    const isConversationalQA = templateData.type === 'CONVERSATIONAL_QA';
    const component = isConversationalQA
      ? {
          type: 'CONVERSATIONAL_QA',
          id: v4(),
          name: `${templateData.name} component`,
          agentConfigurationId: templateData.components?.[0]?.agentConfigurationId,
        }
      : {
          type: 'QA_PAIR',
          id: v4(),
          name: `${templateData.name} component`,
          datastoreQueryId: templateData.datastoreQueries?.[0]?.id,
        };

    const newItem: AgentStudioTemplate = {
      ...templateData,
      [idProp]: v4(),
      createdBy: 'mock@user.com',
      creationDate: new Date().toISOString(),
      lastModifiedBy: 'mock@user.com',
      lastModified: new Date().toISOString(),
      layout: {},
      components: [component],
      datastoreQueries: isConversationalQA ? null : templateData.datastoreQueries
    };

    const newData = [...data, newItem];
    invoke(this.store, 'setData', newData, { action: 'add', item: newItem });
    return newItem;
  }

  /**
   * @override
   */
  async updateItem(item: any): Promise<AgentStudioTemplate> {
    // the store maps the data to the templates object
    // it should handle the templates object update internally
    const { data, idProp } = this.store.meta;
    const itemIndex = findIndex(data, { [idProp]: item[idProp] });

    data[itemIndex] = {
      ...item,
      id: v4(),
      lastModifiedBy: 'mock2@user.com',
      lastModified: new Date().toISOString(),
    };
    const newData = [...data];
    invoke(this.store, 'setData', newData, { action: 'update', item });

    return data[itemIndex];
  }

  /**
   * @override
   */
  async removeItem(id: any): Promise<any> {
    const { data, idProp } = this.store.meta;
    const item = remove(data, { [idProp]: id });
    const newTemplates = [...data];

    invoke(this.store, 'setData', newTemplates, { action: 'remove', item: first(item) });

    return item;
  }

  getComponents(): AgentStudioComponent[] {
    return this.mocks.components;
  }

  getComponentType(type: string): AgentStudioComponent | undefined {
    return find(this.mocks.components, component => component.type.toLowerCase() === type?.toLowerCase());
  }

  getAISolutionPacks(): Promise<SolutionPack[]> {
    return Promise.resolve(this.mocks.AISolutionPacks || []);
  }

  getDatastores(): TemplateDatastore[] {
    return this.mocks.datastores;
  }

  getDatastore(id): TemplateDatastore | undefined {
    return this.mocks.datastores.find(datastore => datastore.id === id);
  }

  createDatastore(datastore): TemplateDatastore {
    // Backend adds these fields
    const newDatastore = {
      ...datastore,
      id: v4(),
    };

    this.mocks.datastores.push(newDatastore);
    return newDatastore;
  }

  updateDatastore(id, datastore): TemplateDatastore | null {
    const index = this.mocks.datastores.findIndex(ds => ds.id === id);
    if (index === -1) { return null; }

    const updatedDatastore = {
      ...this.mocks.datastores[index],
      ...datastore,
    };

    this.mocks.datastores[index] = updatedDatastore;
    return updatedDatastore;
  }

  deleteDatastore(id): TemplateDatastore | undefined {
    const datastore = this.mocks.datastores.find(ds => ds.id === id);
    this.mocks.datastores = this.mocks.datastores.filter(ds => ds.id !== id);
    return datastore;
  }

  getIndexTargets(_customerId): IndexTarget[] {
    return this.mocks.indexTargets;
  }

  getIndexTarget(_customerId, id): IndexTarget | undefined {
    return this.mocks.indexTargets.find(indexTarget => indexTarget.id === id);
  }

  createIndexTarget(_customerId, indexTarget): IndexTarget {
    // Backend adds these fields
    const newIndexTarget = {
      ...indexTarget,
      id: v4(),
      domain: indexTarget.domain || 'default',
      cells: ['data-1-dev'],
      createdBy: 'mock@user.com',
      creationDate: new Date().toISOString(),
      lastModifiedBy: 'mock@user.com',
      lastModified: new Date().toISOString(),
      state: 'ACTIVE',
    };

    this.mocks.indexTargets.push(newIndexTarget);
    return newIndexTarget;
  }

  updateIndexTarget(_customerId, id, indexTarget): IndexTarget | null {
    const index = this.mocks.indexTargets.findIndex(target => target.id === id);
    if (index === -1) { return null; }

    const updatedTarget = {
      ...this.mocks.indexTargets[index],
      ...indexTarget,
      lastModified: new Date().toISOString(),
      lastModifiedBy: 'mock@user.com',
    };

    this.mocks.indexTargets[index] = updatedTarget;
    return updatedTarget;
  }

  deleteIndexTarget(_customerId, id): IndexTarget | undefined {
    const indexTarget = this.mocks.indexTargets.find(target => target.id === id);
    this.mocks.indexTargets = this.mocks.indexTargets.filter(target => target.id !== id);
    return indexTarget;
  }

  getIndexTargetStats(_customerId: string, _indexTargetId: string): Promise<any> {
    const docsCount = 15;

    return Promise.resolve({
      totalDocs: docsCount,
      groups: [
        {
          groupingField: 'index_target_id',
          values: [
            { id: 'mock_value_id_1', docsCount: 10 },
            { id: 'mock_value_id_2', docsCount: 0 },
          ],
        },
      ],
    });
  }

  getAgentConfiguration(id: string): Promise<AgentConfiguration> {
    const agentConfig = this.mocks.AgentConfigurations?.find(config => config.id === id);
    
    if (agentConfig) {
      return Promise.resolve(agentConfig);
    }
    
    if (id === 'agent-configuration-1') {
      return Promise.resolve({
        agentType: ComponentType.CONVERSATIONAL_QA,
        id: 'agent-configuration-1',
        createdBy: 'user@lucidworks.com',
        creationDate: '2025-11-26T21:27:43.235888Z',
        lastModifiedBy: 'user@lucidworks.com',
        lastModified: '2025-11-26T21:27:43.548060Z',
        name: 'Conversational QnA Agent',
        indexTargetId: '123',
        conversationModelId: 'llama-3-8b-instruct',
        domain: 'retail',
      });
    }
    return Promise.resolve(null);
  }

  getAgentConfigurations(): Promise<any> {
    return Promise.resolve(this.mocks.AgentConfigurations || []);
  }

  updateAgentConfiguration(id: string, data) {
    const index = this.mocks.AgentConfigurations.findIndex(config => config.id === id);
    if (index === -1) { return null; }

    const updatedAgentConfig = {
      ...this.mocks.AgentConfigurations[index],
      ...data,
    };

    this.mocks.AgentConfigurations[index] = updatedAgentConfig;
    return updatedAgentConfig;
  }

  deleteAgentConfiguration(id: string) {
    const agentConfig = this.mocks.AgentConfigurations.find(config => config.id === id);
    this.mocks.AgentConfigurations = this.mocks.AgentConfigurations.filter(config => config.id !== id);
    return agentConfig;
  }

  createAgentConfiguration(data): Promise<any> {
    // Backend adds these fields
    const newAgentConfig = {
      ...data,
      id: v4(),
      createdBy: 'user@lucidworks.com',
      creationDate: '2025-11-26T21:27:43.235888Z',
    };

    this.mocks.AgentConfigurations.push(newAgentConfig);
    return Promise.resolve(newAgentConfig);
  }

  /**
   * @override
   */
  protected async getItem(id: string): Promise<AgentStudioTemplate> {
    const { templates, idProp } = this.store.meta;
    const item = find(templates, { [idProp]: id });
    if (item) {
      return item;
    }
  }

  getNewestTemplateVersion(id: string): Promise<AgentStudioTemplate> {
    const { templates } = this.store.meta;
    const item = find(templates, { initialId: id });
    if (item) {
      return item;
    }
  }

  getTemplateHistory(id: string): Promise<{ content: AgentStudioTemplate[] }> {
    const { templates } = this.store.meta;
    // Find all templates with the same initialId or id
    const versionsById = templates.filter(template =>
      template.initialId === id || template.id === id
    );

    // If no versions found by initialId, try to find a single template by id
    if (versionsById.length === 0) {
      const singleTemplate = find(templates, { id });
      if (singleTemplate) {
        return Promise.resolve({
          content: [singleTemplate]
        });
      }
    }

    return Promise.resolve({
      content: versionsById.length > 0 ? versionsById : []
    });
  }
}
